import { formatSignedRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { useRef, useState } from 'react'
import MFAccordion from '../../../../../../../components/ui-components/MFAccordion'
import type { LumpsumPlanRecommendationTaxData } from '../../api/recommendation.types'
import { scrollPlanRecommendationSheetToBottom } from '../sheet/planRecommendationSheetScroll'
import { ExitLoadTaxLiabilityAccordionBody } from './ExitLoadTaxLiabilityAccordionBody'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'

export enum TaxLiabilityAccordionState {
  PARTIAL_WITHDRAWAL = 'PARTIAL_WITHDRAWAL',
  FULL_WITHDRAWAL = 'FULL_WITHDRAWAL'
}

function deriveTaxLiabilityState({
  currentValue,
  withdrawalAmount
}: {
  currentValue: number | null
  withdrawalAmount: number
}): TaxLiabilityAccordionState {
  if ((currentValue ?? 0) > withdrawalAmount) {
    return TaxLiabilityAccordionState.PARTIAL_WITHDRAWAL
  }
  return TaxLiabilityAccordionState.FULL_WITHDRAWAL
}

function resolveTaxLiabilityAmounts({
  taxData,
  withdrawalAmount,
  currentValue
}: {
  taxData: LumpsumPlanRecommendationTaxData | null
  withdrawalAmount: number
  currentValue: number | null
}): { withdrawalAmount: number; currentValue: number | null } {
  return {
    withdrawalAmount,
    currentValue: taxData?.currentValue ?? currentValue
  }
}

type ExitLoadTaxLiabilityAccordionProps = {
  taxData: LumpsumPlanRecommendationTaxData | null
  withdrawalAmount: number
  currentValue: number | null
  isBorderTop?: boolean
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}

export function ExitLoadTaxLiabilityAccordion({
  taxData,
  withdrawalAmount,
  currentValue,
  isBorderTop = true,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation,
  fundName = '',
  fundIsin = ''
}: ExitLoadTaxLiabilityAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const scrollAnchorRef = useRef<HTMLDivElement>(null)
  const stcgTaxInfo = taxData?.stcgTaxInfo
  const ltcgTaxInfo = taxData?.ltcgTaxInfo
  const totalExitLoad = taxData?.exitLoad
    ?? (stcgTaxInfo?.exitLoad ?? 0) + (ltcgTaxInfo?.exitLoad ?? 0)
  const totalTaxLiability = taxData?.netTaxLiability
    ?? (stcgTaxInfo?.taxAmount ?? 0) + (ltcgTaxInfo?.taxAmount ?? 0)
  const resolvedAmounts = resolveTaxLiabilityAmounts({
    taxData,
    withdrawalAmount,
    currentValue
  })
  const taxLiabilityState = deriveTaxLiabilityState(resolvedAmounts)

  return (
    <div ref={scrollAnchorRef}>
      <MFAccordion
        title="Exit Load & Tax Liability"
        iconComponent={<ExitLoadTaxLiabilityAccordionIcon />}
        isExpanded={isExpanded}
        onAccordionClick={() => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              ScreenName: analyticsScreen,
              eventId: PlansLumpsumSheetEventIds.TaxAccordion,
              buttonLabel: 'Exit Load & Tax Liability',
              ...(fundName ? { fundName } : {}),
              ...(fundIsin ? { fundIsin } : {})
            }
          })
          const isOpening = !isExpanded
          setIsExpanded(isOpening)
          if (!isOpening) {
            return
          }
          window.setTimeout(() => {
            const anchorElement = scrollAnchorRef.current
            if (anchorElement) {
              scrollPlanRecommendationSheetToBottom(anchorElement)
            }
          }, 400)
        }}
        scrollOnExpand={false}
        isBorderTop={isBorderTop}
        accordionHeaderClassName="!px-5"
        middleContent={(
          <div className="flex flex-col">
            <div className="text-[14px] font-[500] leading-[130%] text-[#232323]">Exit Load & Tax Liability</div>
            <div className="text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
              {`${formatSignedRupeeLabel({ rawAmount: totalTaxLiability })} estimated tax liability`}
            </div>
          </div>
        )}
      >
        <ExitLoadTaxLiabilityAccordionBody
          taxLiabilityState={taxLiabilityState}
          taxData={taxData}
          withdrawalAmount={resolvedAmounts.withdrawalAmount}
          currentValue={resolvedAmounts.currentValue}
          totalExitLoad={totalExitLoad}
          totalTaxLiability={totalTaxLiability}
        />
      </MFAccordion>
    </div>
  )
}

export function ExitLoadTaxLiabilityAccordionIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M29.165 19.999C29.165 14.9368 25.0614 10.833 19.999 10.833C14.9368 10.8331 10.8331 14.9368 10.833 19.999C10.833 25.0614 14.9368 29.165 19.999 29.165C25.0614 29.165 29.165 25.0614 29.165 19.999ZM30.998 19.999C30.998 26.0738 26.0738 30.998 19.999 30.998C13.9244 30.998 9 26.0737 9 19.999C9.0001 13.9245 13.9245 9.0001 19.999 9C26.0737 9 30.998 13.9244 30.998 19.999Z"
        fill="#232323"
      />
      <path
        d="M23.227 15.4716C23.5849 15.1136 24.166 15.1136 24.5239 15.4716C24.8815 15.8294 24.8815 16.4097 24.5239 16.7676L16.7676 24.5239C16.4097 24.8815 15.8294 24.8815 15.4716 24.5239C15.1136 24.166 15.1136 23.5849 15.4716 23.227L23.227 15.4716Z"
        fill="#232323"
      />
      <path
        d="M18.5872 16.8956C18.587 17.8298 17.8298 18.587 16.8956 18.5872C16.0196 18.5872 15.2988 17.9215 15.2121 17.0684L15.2031 16.8956L15.2121 16.722C15.2988 15.8689 16.0196 15.2031 16.8956 15.2031L17.0684 15.2121C17.9215 15.2988 18.5872 16.0196 18.5872 16.8956Z"
        fill="#232323"
      />
      <path
        d="M24.7904 23.1027C24.7901 24.0369 24.0329 24.794 23.0988 24.7943C22.2227 24.7943 21.5019 24.1286 21.4152 23.2754L21.4062 23.1027L21.4152 22.929C21.502 22.0759 22.2227 21.4102 23.0988 21.4102L23.2715 21.4191C24.1247 21.5058 24.7904 22.2266 24.7904 23.1027Z"
        fill="#232323"
      />
    </svg>
  )
}
